import { useCallback, useEffect, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import useLocales from 'hooks/useLocales';
import { useAIOperationCosts } from './useAIOperationCosts';
import { useAICreditAvailability } from 'hooks/useAICreditAvailability';
import {
  getAIErrorMessage,
  postAutoGenerateCultureCompetencies
} from 'services/ai/ai';

const MAX_COMPETENCIES = 5;
const DEFAULT_SCORE = 5;

export interface CultureProfileAIResult {
  title: string;
  labels: string[];
  ids: string[];
  scores: { [label: string]: number };
}

interface UseCultureProfileAIGenerationOptions {
  assessmentByGroup: any[];
  currentTitle: string;
  enabled: boolean;
  onApply: (result: CultureProfileAIResult) => void;
}

export const useCultureProfileAIGeneration = ({
  assessmentByGroup,
  currentTitle,
  enabled,
  onApply
}: UseCultureProfileAIGenerationOptions) => {
  const { t } = useLocales();
  const { cultureProfileCost } = useAIOperationCosts();
  const { isCreditLimited, outOfCreditsTooltip } = useAICreditAvailability(
    enabled ? cultureProfileCost : 0
  );
  const [aiUrl, setAiUrl] = useState('');
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const requestAbortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      requestAbortRef.current?.abort();
    },
    []
  );

  const mapCompetencyIds = useCallback(
    (competencyIds: number[]) => {
      const allCompetencies = assessmentByGroup.flatMap(
        (group: any) => group.competencies || []
      );

      const labels: string[] = [];
      const ids: string[] = [];
      const scores: { [label: string]: number } = {};

      competencyIds.forEach((id) => {
        const competency = allCompetencies.find(
          (item: { id: number; name: string }) => item.id === id
        );
        if (competency && labels.length < MAX_COMPETENCIES) {
          labels.push(competency.name);
          ids.push(String(competency.id));
          scores[competency.name] = DEFAULT_SCORE;
        }
      });

      return { labels, ids, scores };
    },
    [assessmentByGroup]
  );

  const applyAutoGeneratedProfile = useCallback(
    (data: { name?: string; Competencies?: number[]; competencies?: number[] }) => {
      const competencyIds = data.Competencies || data.competencies || [];
      const mapped = mapCompetencyIds(competencyIds);

      if (!mapped.labels.length) {
        throw new Error(t('Something went wrong, please try again later'));
      }

      onApply({
        title:
          currentTitle?.trim().length > 0
            ? currentTitle.trim()
            : data.name || '',
        labels: mapped.labels,
        ids: mapped.ids,
        scores: mapped.scores
      });
    },
    [currentTitle, mapCompetencyIds, onApply, t]
  );

  const runGeneration = useCallback(async () => {
    if (!aiUrl?.trim()) return;

    setAutoGenLoading(true);
    requestAbortRef.current?.abort();
    const abortController = new AbortController();
    requestAbortRef.current = abortController;

    try {
      const data = await postAutoGenerateCultureCompetencies(
        aiUrl.trim(),
        abortController.signal
      );
      applyAutoGeneratedProfile(data);
    } catch (err: any) {
      const isRequestAborted =
        abortController.signal.aborted ||
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED';

      if (isRequestAborted) return;

      enqueueSnackbar(
        getAIErrorMessage(err, t('Something went wrong, please try again later')),
        { variant: 'error' }
      );
    } finally {
      requestAbortRef.current = null;
      setAutoGenLoading(false);
    }
  }, [aiUrl, applyAutoGeneratedProfile, t]);

  const requestGeneration = useCallback(() => {
    if (!aiUrl?.trim() || autoGenLoading || isCreditLimited) return;
    runGeneration();
  }, [aiUrl, autoGenLoading, isCreditLimited, runGeneration]);

  return {
    aiUrl,
    setAiUrl,
    showAiUrlInput: enabled,
    isGeneratingFromUrl: autoGenLoading,
    requestGeneration,
    isCreditLimited,
    outOfCreditsTooltip
  };
};
